import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchBlogs, createBlog, updateBlog, deleteBlog } from '../services/blogs';
import { getStorageUrl } from '../services/api';
import './Blogs.css';

const formatDate = (d) => {
  if (d == null) return '—';
  try {
    const dateStr = typeof d === 'string' ? d : (d.date || d);
    return dateStr ? new Date(dateStr).toLocaleDateString() : '—';
  } catch {
    return '—';
  }
};

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    blog_date: new Date().toISOString().slice(0, 10),
    external_link: '',
    video_url: '',
  });
  const [featuredImageFile, setFeaturedImageFile] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [removeFeaturedImage, setRemoveFeaturedImage] = useState(false);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBlogs();
      const list = Array.isArray(data) ? data : (data?.data ? (Array.isArray(data.data) ? data.data : []) : []);
      setBlogs(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load blogs.');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRemoveFeaturedImage(false);
      setFeaturedImageFile(file);
      setFeaturedImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveFeaturedImage = () => {
    setRemoveFeaturedImage(true);
    setFeaturedImageFile(null);
    if (featuredImagePreview && featuredImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(featuredImagePreview);
    }
    setFeaturedImagePreview(null);
    if (document.getElementById('blog-featured-image')) {
      document.getElementById('blog-featured-image').value = '';
    }
  };

  const handleRemoveVideo = () => {
    setRemoveVideo(true);
    setVideoFile(null);
    setFormData((prev) => ({ ...prev, video_url: '' }));
    if (document.getElementById('blog-video')) {
      document.getElementById('blog-video').value = '';
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRemoveVideo(false);
      setVideoFile(file);
      setFormData((prev) => ({ ...prev, video_url: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!formData.title.trim() || !formData.content.trim() || !formData.blog_date) {
      setError('Title, content, and blog date are required.');
      return;
    }
    try {
      const existingVideoIsPath = editingBlog?.video && !editingBlog.video.startsWith('http');
      const sendVideoUrl = !videoFile && !removeVideo && !(editingBlog && existingVideoIsPath);
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        blog_date: formData.blog_date,
        external_link: formData.external_link.trim() || undefined,
        featured_image: featuredImageFile || undefined,
        video: videoFile || undefined,
        video_url: sendVideoUrl ? (formData.video_url.trim() || '') : undefined,
        remove_featured_image: editingBlog && removeFeaturedImage,
        remove_video: editingBlog && removeVideo,
      };
      if (editingBlog) {
        await updateBlog(editingBlog.id, payload);
        setSuccess('Blog updated successfully.');
      } else {
        await createBlog(payload);
        setSuccess('Blog created successfully.');
      }
      setShowModal(false);
      resetForm();
      loadBlogs();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save blog.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      blog_date: new Date().toISOString().slice(0, 10),
      external_link: '',
      video_url: '',
    });
    setFeaturedImageFile(null);
    setVideoFile(null);
    setRemoveFeaturedImage(false);
    setRemoveVideo(false);
    if (featuredImagePreview && featuredImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(featuredImagePreview);
    }
    setFeaturedImagePreview(null);
    setEditingBlog(null);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || '',
      content: blog.content || '',
      blog_date: blog.blog_date ? blog.blog_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      external_link: blog.external_link || '',
      video_url: blog.video && blog.video.startsWith('http') ? blog.video : '',
    });
    setFeaturedImageFile(null);
    setVideoFile(null);
    setRemoveFeaturedImage(false);
    setRemoveVideo(false);
    setFeaturedImagePreview(blog.featured_image_url ? (getStorageUrl(blog.featured_image) || blog.featured_image_url) : null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await deleteBlog(id);
      setSuccess('Blog deleted successfully.');
      loadBlogs();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete blog.');
    }
  };

  return (
    <div className="admin-blogs-page">
      <AdminPageTitle title="Blogs" />
      <div className="page-header">
        <h1>Blog Management</h1>
        <button type="button" className="btn-primary" onClick={handleAddNew}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Blog Post
        </button>
      </div>

      {error && (
        <div className="blogs-alert blogs-alert-error">
          {error}
          <button type="button" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className="blogs-alert blogs-alert-success">
          {success}
          <button type="button" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="blogs-loading">Loading blogs…</div>
      ) : blogs.length === 0 ? (
        <div className="blogs-empty">
          <p>No blog posts yet. Click &quot;Add Blog Post&quot; to create one.</p>
        </div>
      ) : (
        <div className="blogs-table-wrapper">
          <table className="blogs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.filter((b) => b && b.id != null).map((blog) => (
                <tr key={blog.id}>
                  <td className="blogs-date">{formatDate(blog.blog_date)}</td>
                  <td className="blogs-title-cell">{blog.title ?? '—'}</td>
                  <td className="blogs-actions">
                    <button type="button" className="btn-edit" onClick={() => handleEdit(blog)}>Edit</button>
                    <button type="button" className="btn-delete" onClick={() => handleDelete(blog.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-content--large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBlog ? 'Edit Blog Post' : 'Add Blog Post'}</h2>
              <button type="button" className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="blogs-form">
              <div className="form-group">
                <label htmlFor="blog-title">Title *</label>
                <input
                  type="text"
                  id="blog-title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Blog title"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="blog-date">Blog date *</label>
                <input
                  type="date"
                  id="blog-date"
                  name="blog_date"
                  value={formData.blog_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="blog-content">Content * (HTML allowed)</label>
                <textarea
                  id="blog-content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="Write your blog content. You can use simple HTML (e.g. <p>, <strong>, <a>)."
                  rows={12}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="blog-featured-image">Featured image (optional)</label>
                <input
                  type="file"
                  id="blog-featured-image"
                  accept="image/*"
                  onChange={handleFeaturedImageChange}
                  disabled={removeFeaturedImage}
                />
                {removeFeaturedImage ? (
                  <p className="image-note image-remove-note">
                    Image will be removed on save.
                    <button type="button" className="blogs-undo-remove" onClick={() => setRemoveFeaturedImage(false)}>Keep image</button>
                  </p>
                ) : (
                  <>
                    {featuredImagePreview && (
                      <div className="image-preview">
                        <img src={featuredImagePreview} alt="Preview" />
                        {(editingBlog?.featured_image || featuredImagePreview) && (
                          <button type="button" className="blogs-remove-media" onClick={handleRemoveFeaturedImage}>Remove image</button>
                        )}
                      </div>
                    )}
                    {editingBlog?.featured_image && !featuredImagePreview && (
                      <div className="image-preview">
                        <img src={getStorageUrl(editingBlog.featured_image) || editingBlog.featured_image_url} alt="Current" />
                        <p className="image-note">Current image. Choose a new file to replace.</p>
                        <button type="button" className="blogs-remove-media" onClick={handleRemoveFeaturedImage}>Remove image</button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="blog-video">Video: upload file (optional)</label>
                <input
                  type="file"
                  id="blog-video"
                  accept="video/*"
                  onChange={handleVideoChange}
                  disabled={removeVideo}
                />
                {removeVideo ? (
                  <p className="image-note image-remove-note">
                    Video will be removed on save.
                    <button type="button" className="blogs-undo-remove" onClick={() => setRemoveVideo(false)}>Keep video</button>
                  </p>
                ) : (
                  <>
                    {videoFile && <p className="image-note">Selected: {videoFile.name}</p>}
                    {(editingBlog?.video || videoFile) && (
                      <button type="button" className="blogs-remove-media" onClick={handleRemoveVideo}>Remove video</button>
                    )}
                  </>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="blog-video-url">Or paste video URL (e.g. YouTube) (optional)</label>
                <input
                  type="url"
                  id="blog-video-url"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  disabled={!!videoFile || removeVideo}
                />
              </div>
              <div className="form-group">
                <label htmlFor="blog-external-link">External link (optional)</label>
                <input
                  type="url"
                  id="blog-external-link"
                  name="external_link"
                  value={formData.external_link}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn-primary">{editingBlog ? 'Update' : 'Create'} Blog Post</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blogs;
